import { AppData, GPS_REPORT_INTERVAL_DURATION } from '../data';
import { Observable, firstValueFrom, from, interval, of } from 'rxjs';
import { FlagStopJob, FlagStopPerform, ScheduledFlagStop } from '../types/flag-stop.type';
import { Injectable, inject } from '@angular/core';
import { InspectionItem, InspectionItemResult } from '../types/inspection.type';
import { BaseJob, JobPerform, OnTheWay, buildJobArrive } from '../types/job.type';
import { LoginCredentials, LoginResponse } from '../types/login.type';
import {
  NonServicePeriodActionEnum,
  NonServicePeriodTypeEnum,
} from '../types/non-service-period.type';
import { PassDetails, PassInformationRequestObject } from '../types/pass.type';
import { RouteStatusUpdate, createNewStatusUpdate } from '../types/route-status-update.type';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

import { CallRequest } from '../types/call-request.type';
import { CompleteRoute } from '../types/complete-route.type';
import { Device } from '../types/device.type';
import { DeviceService } from './device.service';
import { Fluids } from '../types/fluids.type';
import { GpsReport } from '../types/gps-report.type';
import { LocalSettings } from '../types/local-settings.type';
import { LocationService } from './location.service';
import { LoggingService } from './logging.service';
import { Logout } from '../types/logout.type';
import { TelemetryService } from './telemetry.service';
import { MessageResponse } from '../types/message-response.type';
import { RegistrationResult } from '../types/registration.type';
import { RouteUpdate } from '../types/route-update.type';
import { SendPredefinedMessage } from '../types/send-predefined-message.type';
import { ServerProxyService } from './server-proxy.service';
import { SessionService } from './session.service';
import { SettingsService } from './settings.service';
import { StoragePluginService } from './plugin/storage-plugin.service';
import { TranslateService } from './translate.service';
import { VersionService } from './version.service';
import { environment } from '../../environments/environment';
import { DateTime } from 'luxon';
import { DisclaimerPage } from '../pages/disclaimer/disclaimer.page';
import { NotificationService } from './notification.service';
import { ModalController } from '@ionic/angular';
import { RouteSummary } from '../types/route-summary.type';
import { GenericRouteEvent, RiderTypeInfo, StopTypeEnum } from '../types';
import { UpdatesService } from './updates.service';
import { RouteSetupService } from './route-setup.service';
import { Router } from '@angular/router';
import { DisableDrivingMode } from '../types/disable-driving-mode.type';

import packageJSON from '../../../package.json';
const version = packageJSON.version;
const currentMajorVersion = Number(version.split('.')[0]);

@Injectable({
  providedIn: 'root',
})
export class DispatcherService {
  private deviceService = inject(DeviceService);
  private locationService = inject(LocationService);
  private loggingService = inject(LoggingService);
  private sessionService = inject(SessionService);
  private storageService = inject(StoragePluginService);
  private proxy = inject(ServerProxyService);
  private translateService = inject(TranslateService);
  private versionService = inject(VersionService);
  private settingsService = inject(SettingsService);
  private notificationService = inject(NotificationService);
  private modalCtrl = inject(ModalController);
  private updatesService = inject(UpdatesService);
  private routeSetupService = inject(RouteSetupService);
  private router = inject(Router);
  private telemetryService = inject(TelemetryService);

  private readonly data = inject(AppData);

  private gpsReportInterval = interval(GPS_REPORT_INTERVAL_DURATION);

  private readonly gtm = (<any>window).dataLayer || [];

  public readonly pendingUpdatesExist$ =
    this.updatesService.pendingUpdatesExist$.pipe(shareReplay());

  private registerDeviceApiCall(device: Device): Observable<RegistrationResult> {
    return this.proxy.registerDevice(device);
  }

  async forceRemovePendingUpdates() {
    return this.updatesService.forceRemovePendingUpdates();
  }

  async registerDevice(): Promise<RegistrationResult> {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_ register_device' });

    const _device = this.data.device;
    const { version } = (await this.versionService.getAppInfo()) || {};
    const { channel } = this.versionService.config || {};
    const { buildId } = this.versionService.snapshotInfo || {};
    let localSettings = this.data.localSettings;

    const device: Device = {
      ..._device,
      description: _device.description || localSettings.description,
      identity: {
        ..._device.identity,
        customerName: _device.identity.customerName || localSettings.customerName,
        customerId: _device.identity.customerId || localSettings.customerId,
      },
      softwareVersion: {
        ionicChannel: channel,
        playStoreVersion: version,
        buildVersion: buildId,
        ionicSnapshotBuildId: buildId,
      },
    };

    this.loggingService.setMetaData(
      { key: 'customerName', value: device.identity.customerName },
      { key: 'description', value: device.description },
    );

    const response = await firstValueFrom(this.registerDeviceApiCall(device));
    const { ionicChannel, customerId, customerName } = response;
    const _channel = this.getChannel(ionicChannel);
    console.debug('------------>', _channel);

    this.versionService.config.channel = _channel;
    device.softwareVersion.ionicChannel = _channel;
    this.versionService.setChannel(_channel);

    localSettings = this.data.localSettings;
    const updatedSettings: LocalSettings = {
      ...localSettings,
      customerId,
      customerName,
      //  backwards compatibility
      //  if we can't ascertain a value, assume production
      isProduction:
        localSettings.isProduction ??
        environment.dispatcherEndpoints.find(ep => ep.name === localSettings.environment)
          ?.isProduction ??
        true,
    };
    this.deviceService.updateDeviceInformation({
      ...device,
      softwareVersion: { ...device.softwareVersion, ionicChannel: _channel },
    });
    await this.settingsService.saveSettings(updatedSettings);
    return response;
  }

  getChannel(channel: string) {
    // server is telling app to use a channel that does not match the binary loaded
    if (channel && !channel.includes(currentMajorVersion.toString())) {
      const _channel = channel.replace(/\d+/, currentMajorVersion.toString());
      return _channel;
    }
    //  server is sending a usable channel
    if (channel && channel !== 'configurable') {
      return channel;
    }
    //  no channel is being specified, therefore we select it based on the environment targeted
    const settings = this.data.localSettings;
    const _environment = environment.dispatcherEndpoints.find(
      ep => ep.url === settings.dispatcherUrl,
    );
    console.log(_environment);
    if (!_environment) {
      return environment.defaultChannel;
    }
    const _channel = environment.channels.find(ch => ch.name === _environment.name);
    if (!_channel) {
      return environment.defaultChannel;
    }
    return _channel.channel;
  }

  async login(loginCredentials: LoginCredentials): Promise<LoginResponse> {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_ login' });
    this.data.set('drivingModePaused', false);

    //  TEMP: band-aid (TTSD-149)
    this.data.set('loginCredentials', loginCredentials);
    const response = await firstValueFrom(this.proxy.login(loginCredentials));

    //  prevent login if user is on a version that is no longer supported -- force the update
    const updateRequired = this.handleUpdateRequired(response);
    if (updateRequired) {
      return;
    }

    //  warn the user that in n number of days, they will be forced to upgrade
    const imminentUpdate = this.handleImminentUpdate(response);
    if (imminentUpdate) {
      this.notificationService.warning(imminentUpdate, null, true);
    }

    //  silently force check for new version
    await this.versionService.checkForNewVersion(true, true).catch(err => {
      console.error('Error checking for new version upon login', err);
    });

    const disclaimerModal = await this.modalCtrl.create({
      component: DisclaimerPage,
      cssClass: 'parascope-full-modal',
      backdropDismiss: false,
    });
    await disclaimerModal.present();

    //  update session and identity information
    await Promise.all([
      this.sessionService.updateSession({
        sessionId: response.sessionId,
        pin: loginCredentials.pin,
      }),
      this.settingsService.saveSettings({ ...(loginCredentials.identity || {}) }),
    ]);

    //  data Setup
    this.data.set('routeSummaries', response.routeSummaries || []);
    this.data.set('rapidPassEnabled', response.rapidPassEnabled);
    this.data.set('paraPassEnabled', response.paraPassEnabled);
    this.data.set('predefinedMessages', response.predefinedMessages || []);
    this.data.set('fareTypes', response.fareTypes || []);
    this.data.set('speedLockThreshold', response.speedLockThreshold ?? Infinity);
    //  configuration setup
    this.locationService.paraScopeGpsReportingInterval = response.paraScopeGpsReportingInterval;
    this.proxy.paraScopeGpsReportingEndpoint = response.paraScopeGpsReportingEndpoint;
    this.proxy.paraScopeGpsReportingEndpointToken = response.paraScopeGpsReportingEndpointToken;
  }

  async addInspectionReport(inspectionItems: InspectionItem[], type: NonServicePeriodTypeEnum) {
    this.data.set('drivingModePaused', false);
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_ inspection_report' });
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    const resultUpdates: InspectionItemResult[] = [];
    const now = new Date();
    const _gpsReport = this.data.gpsReport;
    const gpsReport: GpsReport = {
      ..._gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
    };

    for (const item of inspectionItems) {
      resultUpdates.push({
        inspectionItemId: item.inspectionItemId,
        result: item.questionType === 0 ? item.result.toString() : item.textResult,
        comment: item.comment,
      });
    }
    if (type === NonServicePeriodTypeEnum.PreTrip) {
      update.preTripInspection = resultUpdates;
    } else if (type === NonServicePeriodTypeEnum.PostTrip) {
      update.postTripStart = gpsReport;
      update.postTripEnd = gpsReport;
      update.postTripInspection = resultUpdates;
    }
    update.gpsReports = [...(update.gpsReports || []), gpsReport];
    await this.updatesService.addPendingUpdate({ update, isSignificant: false });
  }

  async addCustomFormResult(customFormResults: InspectionItemResult[]) {
    this.data.set('drivingModePaused', false);
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_ inspection_report' });
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    const now = new Date();
    const _gpsReport = this.data.gpsReport;
    const gpsReport: GpsReport = {
      ..._gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
    };

    update.customFormResults.push(...customFormResults);
    update.gpsReports = [...(update.gpsReports || []), gpsReport];
    this.data.set('route', { ...this.data.route, customFormCompleted: true });
    await this.updatesService.addPendingUpdate({ update, isSignificant: false });
  }

  async completeRoute(driverSignature: string, _gpsReport: GpsReport) {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_ complete_route' });
    this.data.set('drivingModePaused', false);
    const now = new Date();

    const gpsReport: GpsReport = {
      ..._gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
    };

    const request: CompleteRoute = {
      sessionId: this.data.sessionId,
      driverSignature: driverSignature,
      gpsReport,
    };

    this.locationService.logToTrackJS(_gpsReport, gpsReport);

    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    update.completeRoute = request;
    update.gpsReports = [...(update.gpsReports || []), gpsReport];

    const routeId = this.data.route.id;
    const routeSummaries = this.data.routeSummaries.filter(rs => rs.id !== routeId);
    this.data.set('routeSummaries', routeSummaries);

    await this.updatesService.addPendingUpdate({ update, isSignificant: true });
    this.data.set('driverIsActivelyReporting', false);
    this.telemetryService.stopTelemetry();
    this.data.set('route', {});
  }

  async onTheWays(onTheWays: OnTheWay[]) {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_on_the_way' });
    this.data.set('drivingModePaused', false);

    const _gpsReport = this.data.gpsReport;
    const now = new Date();
    const gpsReport: GpsReport = {
      ..._gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
    };
    this.locationService.logToTrackJS(_gpsReport, gpsReport);
    for (const arrival of onTheWays) {
      arrival.gpsReport = gpsReport;
    }
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    update.onTheWays = onTheWays;
    update.gpsReports = [...(update.gpsReports || []), gpsReport];
    await this.updatesService.addPendingUpdate({ update, isSignificant: true });
  }

  async arriveJobs(jobs: GenericRouteEvent[]) {
    this.data.set('drivingModePaused', false);
    if (!jobs.length) {
      return;
    }
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_arrive_jobs' });
    const arriveTime = new Date();
    const gpsReport: GpsReport = {
      ...this.data.gpsReport,
      dateTimeReported: arriveTime,
      occuranceDate: arriveTime,
    };
    this.locationService.logToTrackJS(this.data.gpsReport, gpsReport);
    const jobArrivals = jobs.map(job => buildJobArrive({ job: job.value, gpsReport }));
    const update: RouteStatusUpdate = {
      ...createNewStatusUpdate(this.data.sessionId),
      jobArrivals,
      gpsReports: [gpsReport],
    };
    jobs.forEach(job => {
      job.value.arriveTime = arriveTime.toISOString();
      job.actions = job.getActions();
    });
    this.data.setJobs(jobs.map(j => j.value));

    if (jobs.some(j => j.value.stopType === StopTypeEnum.PULL_OUT)) {
      this.data.set('route', { ...this.data.route, hasBeenStarted: true });
    }
    await this.updatesService.addPendingUpdate({ update, isSignificant: true });
  }

  async logout(): Promise<void> {
    console.debug('Start logout fn');
    this.gtm.push({ event: 'custom_event ', custom_event_name: 'perform_ logout' });
    this.data.set('driverIsActivelyReporting', false);
    this.telemetryService.stopTelemetry();
    await this.locationService.stop();
    this.data.set('drivingModePaused', true);
    const _gpsReport: GpsReport = this.data.gpsReport;
    const now = new Date();
    const gpsReport: GpsReport = {
      ..._gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
    };
    this.locationService.logToTrackJS(_gpsReport, gpsReport);
    this.versionService.resetUpgradeVariables();

    const request: Logout = { sessionId: this.data.sessionId, gpsReport: gpsReport };
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    update.logout = request;
    update.gpsReports = [...(update.gpsReports || []), gpsReport];

    await this.updatesService.addPendingUpdate({ update, isSignificant: false });

    console.debug('End logout fn');
    this.router.navigate(['/login']);
    console.debug('End logout navigated');
  }

  async performJob(jobPerform: JobPerform) {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_ job' });
    this.data.set('drivingModePaused', false);

    const _gpsReport = this.data.gpsReport;
    const now = new Date();
    const gpsReport = {
      ..._gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
    };
    jobPerform.gpsReport = gpsReport;
    this.locationService.logToTrackJS(_gpsReport, gpsReport);

    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    update.jobPerforms.push(jobPerform);
    update.gpsReports = [...(update.gpsReports || []), gpsReport];
    // push the new data
    await this.updatesService.addPendingUpdate({ update, isSignificant: true });
  }

  async performNonServicePeriod(
    action: NonServicePeriodActionEnum,
    type: NonServicePeriodTypeEnum,
    overrideDate: Date = null,
  ) {
    this.data.set('drivingModePaused', false);
    console.log(action, type, overrideDate);
    const _gpsReport = this.data.gpsReport;
    const now = new Date();
    const gpsReport: GpsReport = {
      ..._gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
    };
    this.locationService.logToTrackJS(_gpsReport, gpsReport);
    if (overrideDate) {
      gpsReport.occuranceDate = overrideDate;
    }
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    update.gpsReports = [...(update.gpsReports || []), gpsReport];

    switch (type) {
      case NonServicePeriodTypeEnum.PreTrip:
        if (action === NonServicePeriodActionEnum.Start) {
          update.preTripStart = gpsReport;
          this.data.set('route', {
            ...this.data.route,
            preTrip: {
              ...this.data.route.preTrip,
              hasBeenStarted: true,
              arrivedTime: Date.now().toString(),
            },
          });
          this.gtm.push({ event: 'custom_event', custom_event_name: 'start_ pre_trip' });
        } else {
          update.preTripEnd = gpsReport;
          this.data.set('route', {
            ...this.data.route,
            preTrip: {
              ...this.data.route.preTrip,
              hasBeenCompleted: true,
              departedTime: Date.now().toString(),
            },
          });
          this.gtm.push({ event: 'custom_event', custom_event_name: 'complete_ pre_trip' });
        }
        break;
      case NonServicePeriodTypeEnum.Break:
        if (action === NonServicePeriodActionEnum.Start) {
          update.breakStart = gpsReport;
          this.data.set('route', {
            ...this.data.route,
            break: { ...this.data.route.break, hasBeenStarted: true },
          });
          this.gtm.push({ event: 'custom_event', custom_event_name: 'start_ break' });
        } else {
          update.breakEnd = gpsReport;
          this.data.set('route', {
            ...this.data.route,
            break: { ...this.data.route.break, hasBeenCompleted: true },
          });
          this.gtm.push({ event: 'custom_event', custom_event_name: 'complete_ break' });
        }
        break;
      case NonServicePeriodTypeEnum.PostTrip:
        if (action === NonServicePeriodActionEnum.Start) {
          update.postTripStart = gpsReport;
          this.data.set('route', {
            ...this.data.route,
            postTrip: { ...this.data.route.postTrip, hasBeenStarted: true },
          });
          this.gtm.push({ event: 'custom_event', custom_event_name: 'start_ post_trip' });
        } else {
          update.postTripEnd = gpsReport;
          this.data.set('route', {
            ...this.data.route,
            postTrip: { ...this.data.route.postTrip, hasBeenCompleted: true },
          });
          this.gtm.push({ event: 'custom_event', custom_event_name: 'complete_ post_trip' });
        }
        break;
    }

    await this.updatesService.addPendingUpdate({ update, isSignificant: true });
  }

  async departJobs(jobs: GenericRouteEvent[]) {
    this.data.set('drivingModePaused', false);
    if (!jobs.length) {
      return;
    }
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_depart_jobs' });
    const departedTime = new Date();
    const gpsReport: GpsReport = {
      ...this.data.gpsReport,
      dateTimeReported: departedTime,
      occuranceDate: departedTime,
    };
    this.locationService.logToTrackJS(this.data.gpsReport, gpsReport);
    const jobDepartures = jobs.map(job => buildJobArrive({ job: job.value, gpsReport }));
    const update: RouteStatusUpdate = {
      ...createNewStatusUpdate(this.data.sessionId),
      jobDepartures,
      gpsReports: [gpsReport],
    };
    jobs.forEach(job => {
      job.value.departedTime = departedTime.toISOString();
      job.value.completed = true;
      job.completed = true;
    });
    this.data.setJobs(jobs.map(j => j.value));
    await this.updatesService.addPendingUpdate({ update, isSignificant: true });
  }

  async dismissDispatchMessage(response: MessageResponse) {
    this.data.set('drivingModePaused', false);
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    update.messageResponses.push(response);
    await this.updatesService.addPendingUpdate({ update, isSignificant: false });
  }

  async predefinedMessage(message: SendPredefinedMessage) {
    this.data.set('drivingModePaused', false);
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    update.predefinedMessages.push(message);
    await this.updatesService.addPendingUpdate({ update, isSignificant: false });
  }

  /** Sends a free form message back to dispatch */
  async freeFormMessage(message: string): Promise<void> {
    const now = new Date();
    const gpsReport: GpsReport = {
      ...this.data.gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
    };
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    update.freeformMessages.push({ gpsReport, message });
    await this.updatesService.addPendingUpdate({ update, isSignificant: false });
    this.data.set('drivingModePaused', false);
  }

  // begin route status update functions
  async callRequest(request: CallRequest) {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_call_request' });
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    update.callRequests.push(request);
    await this.updatesService.addPendingUpdate({ update, isSignificant: false });
  }

  /** Perform a flagstop for stops using FareTypes (ecolane) */
  async performFareTypesFlagStop(flagStopPerform: FlagStopPerform): Promise<void> {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_flag_stop' });
    this.data.set('drivingModePaused', false);
    // ensure the current number onboard is accurate in between route updates
    let currentNumberOnboard = this.data.route.currentNumberOnboard || 0;
    if (flagStopPerform.jobs?.length) {
      for (const job of flagStopPerform.jobs) {
        currentNumberOnboard += job.onboarded ?? 0;
      }
    }
    currentNumberOnboard -= flagStopPerform.deboarded ?? 0;
    this.data.set('route', { ...this.data.route, currentNumberOnboard });
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    this.locationService.logToTrackJS(flagStopPerform.gpsReport, this.data.gpsReport);
    update.flagStopPerforms.push(flagStopPerform);
    await this.updatesService.addPendingUpdate({ update, isSignificant: true });
  }

  /** Perform a flagstop for stops using RiderTypes (cts) */
  async performRiderTypesFlagStop({
    flagStop,
    jobs = [],
    odometer,
    updatedRiderTypes,
  }: {
    flagStop: ScheduledFlagStop;
    jobs?: FlagStopJob[];
    odometer?: number;
    updatedRiderTypes?: RiderTypeInfo[];
  }): Promise<void> {
    this.gtm.push({
      event: 'custom_event',
      custom_event_name: flagStop?.id ? 'perform_scheduled_stop' : 'perform_flag_stop',
    });
    this.data.set('drivingModePaused', false);

    const now = new Date();
    const gpsReport: GpsReport = {
      ...this.data.gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
      odometer: odometer || this.data.gpsReport.odometer,
    };
    this.locationService.logToTrackJS(gpsReport, this.data.gpsReport);

    const { id, departureTime, arrivalTime } = flagStop || {};
    const flagStopPerform: FlagStopPerform = <FlagStopPerform>{
      completedDate: now,
      gpsReport,
      scheduledFlagStopId: id,
      scheduledDepartureDate: departureTime,
      scheduledArrivalDate: arrivalTime,
      jobs,
    };

    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    update.flagStopPerforms.push(flagStopPerform);
    update.gpsReports = [...(update.gpsReports || []), gpsReport];

    const _flagStop =
      this.data.scheduledFlagStops.find(fs => fs.id === id) || <ScheduledFlagStop>{};
    if (_flagStop) _flagStop.performed = true;

    if (updatedRiderTypes)
      this.data.set('route', { ...this.data.route, riderTypes: updatedRiderTypes });
    this.data.set('scheduledFlagStops', this.data.scheduledFlagStops);
    await this.updatesService.addPendingUpdate({ update, isSignificant: true });
  }

  async fluidsReport(request: Fluids) {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_fluid_report' });
    this.data.set('drivingModePaused', false);
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    update.fluids = request;
    await this.updatesService.addPendingUpdate({ update, isSignificant: false });
  }

  async gpsReport(_gpsReport: GpsReport) {
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    const now = new Date();
    const gpsReport: GpsReport = {
      ..._gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
    };
    this.locationService.logToTrackJS(_gpsReport, gpsReport);
    update.gpsReports.push(gpsReport);
    await this.updatesService.addPendingUpdate({ update, isSignificant: false });
  }

  async disableDrivingMode() {
    if (
      !this.data.route.id ||
      !this.data.driverIsActivelyReporting ||
      this.data.currentRouteIsPreview
    ) {
      console.warn('not logged into a route, therefore not a valid scenario');
      return;
    }
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_disable_driving_mode' });
    const update: RouteStatusUpdate = createNewStatusUpdate(this.data.sessionId);
    const now = new Date();
    const gpsReport = {
      ...this.data.gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
    };
    const disableDrivingMode: DisableDrivingMode = {
      device: this.data.device,
      gpsReport,
      speed: this.data.spoofSpeed ? (this.data.spoofSpeed.current ?? 0) : this.data.gpsReport.speed,
      isMoving: this.data.gpsReport.speed > 0,
      speedAccuracy: this.data.gpsReport.confidence,
      performedTime: now.toISOString(),
    };
    update.gpsReports.push(gpsReport);
    update.disabledDrivingModes.push(disableDrivingMode);
    await this.updatesService.addPendingUpdate({ update, isSignificant: false });
  }

  async getFlagStopPassInformation(passId: string): Promise<PassDetails> {
    this.data.set('drivingModePaused', false);
    const currentGps = this.data.gpsReport;
    const passInformationRequest: PassInformationRequestObject = {
      sessionId: this.data.sessionId,
      rideId: 0,
      passId: passId,
      numberOfchildren: 0,
      numberOfEscorts: 0,
      latitude: currentGps.latitude,
      longitude: currentGps.longitude,
    };

    const response = await this.proxy.passInformation(passInformationRequest);
    return response;
  }

  async getPassInformation(passId: string, job: BaseJob): Promise<PassDetails> {
    if (!this.data.connectionStatus?.connected) {
      return null;
    }
    const gpsReport = this.data.gpsReport;
    const passInformationRequest: PassInformationRequestObject = {
      sessionId: this.data.sessionId,
      rideId: job.rideId,
      passId: passId,
      numberOfchildren: job.numberOfChildren,
      numberOfEscorts: job.escorts,
      latitude: gpsReport.latitude,
      longitude: gpsReport.longitude,
    };

    const response = await this.proxy.passInformation(passInformationRequest).catch(err => {
      console.error(err);
      return null;
    });
    return response;
  }

  async isTripSync(customerId: number): Promise<Boolean> {
    return this.proxy.tripSync(customerId);
  }

  canStartRoute({ scheduledStart, vehicleNumber }: RouteSummary): boolean {
    this.data.set('drivingModePaused', false);
    if (environment.features.canStartRouteLimit === null) {
      return true;
    }
    const diff = DateTime.now().diff(DateTime.fromISO(scheduledStart as any), 'hours');
    const timeDiffInHours = diff.isValid ? diff.hours : -1000;
    //  Driver can't be more than 4 hours early for the route
    return timeDiffInHours > -environment.features.canStartRouteLimit && !!vehicleNumber;
  }

  async startRoute(routeId: number): Promise<RouteUpdate | null> {
    this.data.set('drivingModePaused', false);
    this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_ start_route' });

    let _routeId = routeId;

    //  TEMP: band-aid (TTSD-149)
    if (environment.features.refreshDataBeforeRouteStart) {
      if (!this.data.loginCredentials) {
        await this.logout();
        return null;
      }
      //  refresh the session & get new route summaries
      await this.login(this.data.loginCredentials);
      //  filter summaries for the ones that can actually be started
      const canStartRoutes = this.data.routeSummaries?.filter(r => this.canStartRoute(r)) || [];
      const matchingRoute = canStartRoutes.find(r => r.id === routeId);
      if (matchingRoute) _routeId = matchingRoute.id;
      //  have multiple or no viable options either way, can't figure out what the correct course of actions is
      else if (this.canStartRoute.length !== 1) {
        await this.logout();
        return null;
      }
      //  only one available option, even though IDs doesn't match, assume this is the actual correct route ID
      else _routeId = canStartRoutes?.[0].id;
    }

    if (this.deviceService.isNativeDevice) {
      await this.locationService.restartBBGO();
    }

    const _gpsReport = this.data.gpsReport;
    const now = new Date();
    const gpsReport: GpsReport = {
      ..._gpsReport,
      dateTimeReported: now,
      occuranceDate: now,
    };
    this.locationService.logToTrackJS(_gpsReport, gpsReport);
    const routeUpdate = await this.proxy.startRoute(_routeId, gpsReport);
    return this.routeSetup(routeUpdate);
  }

  async previewRoute(routeId: number): Promise<RouteUpdate> {
    this.data.set('drivingModePaused', false);
    const routeUpdate = await this.proxy.routePreview(routeId);
    return this.routeSetup(routeUpdate);
  }

  async routeSetup(routeUpdate: RouteUpdate) {
    try {
      const { route } = routeUpdate || {};
      if (!route.routeNumber || !route.id) {
        await this._handleRouteSetupFailure();
        return routeUpdate;
      }
      return this.routeSetupService.routeSetup(routeUpdate);
    } catch (err) {
      console.error(err);
      await this._handleRouteSetupFailure();
      return routeUpdate;
    }
  }

  private async _handleRouteSetupFailure() {
    if (environment.features.dumpUpdatesOnForcedLogout) {
      await this.forceRemovePendingUpdates();
    }
    await this.logout();
    this.notificationService.warning(this.translateService.translate('MESSAGE.logout.noRun'));
    this.router.navigate(['/login']);
  }

  async completePreTripInspection(inspectionItems: InspectionItem[], startedDate: Date) {
    this.data.set('drivingModePaused', false);
    await Promise.all([
      this.addInspectionReport(inspectionItems, NonServicePeriodTypeEnum.PreTrip),
      this.performNonServicePeriod(
        NonServicePeriodActionEnum.End,
        NonServicePeriodTypeEnum.PreTrip,
      ),
    ]);
  }

  public reportGps(): Observable<void> {
    // Start live telemetry when driver becomes active
    this.telemetryService.startTelemetry();
    return from(this.storageService.ready()).pipe(
      switchMap(() => this.gpsReportInterval),
      switchMap(() => firstValueFrom(this.data.gpsReport$)),
      switchMap(gpsReport => this.gpsReport(gpsReport)),
      catchError(err => {
        console.error(err);
        return of(null);
      }),
      map(() => {}),
    );
  }

  /**
   * handles required update notification
   * @returns whether the user can proceed with login in
   */
  handleUpdateRequired({ daysRemainingBeforeUpgradeIsRequired, expirationNotice }: LoginResponse) {
    if (daysRemainingBeforeUpgradeIsRequired === null || daysRemainingBeforeUpgradeIsRequired > 0) {
      return false;
    }

    const storeAccessMessage =
      `<br><br>${this.translateService.translate('LABEL.doNotHavePermission')} ` +
      `${!this.deviceService.isIos ? 'Play' : 'App'} ${this.translateService.translate('LABEL.notifyAdmin')}.`;

    this.versionService.forceUpdateBeforeLogin = true;
    this.versionService.showPlayStoreUpgradeButton = true;
    this.versionService.upgradeMessage =
      this.translateService.translate('UPDATE.upgrade') +
      `${this.translateService.translate(!this.deviceService.isIos ? 'UPDATE.playStore' : 'UPDATE.appStore')}.\r\n` +
      (expirationNotice ? expirationNotice + '\r\n' : '') +
      `              ${storeAccessMessage}`;

    // login is denied
    return true;
  }

  handleImminentUpdate({
    daysRemainingBeforeUpgradeIsRequired,
    expirationNotice,
  }: LoginResponse): null | string {
    if (!daysRemainingBeforeUpgradeIsRequired) {
      return null;
    }

    const storeAccessMessage =
      `<br><br>${this.translateService.translate('LABEL.doNotHavePermission')} ` +
      `${!this.deviceService.isIos ? 'Play' : 'App'} ${this.translateService.translate('LABEL.notifyAdmin')}.`;

    this.versionService.showPlayStoreUpgradeButton = true;
    let daysRemaining = `${daysRemainingBeforeUpgradeIsRequired} ${this.translateService.translate('LABEL.days')}`;
    if (daysRemainingBeforeUpgradeIsRequired === 1) {
      daysRemaining = this.translateService.translate('LABEL.today');
    }
    this.versionService.upgradeMessage =
      (expirationNotice
        ? expirationNotice + '\r\n'
        : this.translateService.translate('UPDATE.immediateUpgrade')) +
      `            ${this.translateService.translate('UPDATE.unableToUse')} ${daysRemaining}.\r\n` +
      `            ${storeAccessMessage}`;
    return this.versionService.upgradeMessage;
  }

  public async updateRouteSummaries(): Promise<void> {
    const summaries = await this.proxy.routeSummaries();
    this.data.set('routeSummaries', summaries);
  }
}
